import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiClient, API_ENDPOINTS } from '../api/config';
import type { Release } from '../types';
import {
  Card,
  CardContent,
  Input,
  Badge,
  Button,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  Skeleton,
  EmptyState,
  Alert,
} from '@license-management/ui';
import { Search, Download, Package, AlertCircle, Sparkles } from 'lucide-react';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getPlatformName(platform: string): string {
  if (platform === 'darwin') return 'macOS';
  if (platform === 'win32') return 'Windows';
  if (platform === 'linux') return 'Linux';
  return platform;
}

function PlatformIcon({
  platform,
  className = 'h-6 w-6',
  colored = false,
}: {
  platform: string;
  className?: string;
  colored?: boolean;
}) {
  if (platform === 'darwin') {
    return (
      <svg
        className={`${className} ${colored ? 'text-zinc-800' : 'text-current'}`}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
    );
  }
  if (platform === 'win32') {
    return (
      <svg
        className={`${className} ${colored ? 'text-blue-500' : 'text-current'}`}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
      </svg>
    );
  }
  if (platform === 'linux') {
    return (
      <svg
        className={`${className} ${colored ? 'text-orange-500' : 'text-current'}`}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139z" />
      </svg>
    );
  }
  return null;
}

function DownloadsPage() {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOS, setSelectedOS] = useState<string>('all');
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['releases'],
    queryFn: () => apiClient.get<Release[]>(API_ENDPOINTS.releases),
  });

  // Filter versions based on search and OS filter
  const filteredVersions = useMemo(() => {
    if (!data) return [];

    const matchingReleases = data.filter((release) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !release.version.toLowerCase().includes(query) &&
          !getPlatformName(release.platform).toLowerCase().includes(query)
        ) {
          return false;
        }
      }
      if (selectedOS !== 'all' && release.platform !== selectedOS) return false;
      return true;
    });

    const versions = [...new Set(matchingReleases.map((r) => r.version))];
    return versions.sort((a, b) => {
      const aParts = a.split('.').map(Number);
      const bParts = b.split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if ((bParts[i] || 0) !== (aParts[i] || 0))
          return (bParts[i] || 0) - (aParts[i] || 0);
      }
      return 0;
    });
  }, [data, searchQuery, selectedOS]);

  // Get releases for selected version
  const selectedVersionReleases = useMemo(() => {
    if (!data || !selectedVersion) return [];
    return data.filter((release) => {
      if (release.version !== selectedVersion) return false;
      if (selectedOS !== 'all' && release.platform !== selectedOS) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !release.version.toLowerCase().includes(query) &&
          !getPlatformName(release.platform).toLowerCase().includes(query)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [data, selectedVersion, selectedOS, searchQuery]);

  // Auto-select first version when filter changes
  useMemo(() => {
    if (
      filteredVersions.length > 0 &&
      (!selectedVersion || !filteredVersions.includes(selectedVersion))
    ) {
      setSelectedVersion(filteredVersions[0]);
    }
  }, [filteredVersions, selectedVersion]);

  const handleDownload = async (release: Release) => {
    setDownloading(release.releaseId);
    try {
      const result = await apiClient.download(
        API_ENDPOINTS.releaseDownload(release.releaseId)
      );
      // Validate URL to prevent XSS - only allow http/https protocols
      let downloadUrl: URL;
      try {
        downloadUrl = new URL(result.url);
        if (!['http:', 'https:'].includes(downloadUrl.protocol)) {
          throw new Error('Invalid download URL protocol');
        }
      } catch {
        toast.error('Invalid download URL received from server');
        return;
      }
      const link = document.createElement('a');
      link.href = downloadUrl.href;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Starting download: ${result.fileName}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Downloads" />
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-64" />
          <div className="flex gap-6">
            <div className="w-48">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-8 mb-2" />
              ))}
            </div>
            <div className="flex-1 grid gap-4 md:grid-cols-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-48" variant="rectangular" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
        Failed to load downloads. Please try again later.
      </Alert>
    );
  }

  return (
    <div>
      <PageHeader
        title="Downloads"
        description="Download the latest versions of your software"
      />

      {/* Search Bar */}
      <div className="mb-4">
        <Input
          placeholder="Search versions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftElement={<Search className="h-5 w-5" />}
        />
      </div>

      {/* OS Filter Tabs */}
      <div className="mb-6">
        <Tabs value={selectedOS} onValueChange={setSelectedOS}>
          <TabsList>
            <TabsTrigger value="all">All Platforms</TabsTrigger>
            <TabsTrigger value="win32">Windows</TabsTrigger>
            <TabsTrigger value="darwin">macOS</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {filteredVersions.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Package className="h-12 w-12" />}
              title="No downloads available"
              description={
                searchQuery || selectedOS !== 'all'
                  ? 'No releases match your filters.'
                  : 'Check back later for new releases.'
              }
              action={
                (searchQuery || selectedOS !== 'all') && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedOS('all');
                    }}
                  >
                    Clear filters
                  </Button>
                )
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-6">
          {/* Version Sidebar */}
          <Card className="w-52 flex-shrink-0 h-fit">
            <CardContent className="p-3">
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider px-2 mb-2">
                Versions
              </h3>
              <nav className="space-y-0.5 max-h-[calc(100vh-350px)] overflow-y-auto">
                {filteredVersions.map((version, index) => (
                  <button
                    key={version}
                    onClick={() => setSelectedVersion(version)}
                    className={`w-full text-left px-3 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                      selectedVersion === version
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold shadow-glow-primary'
                        : 'text-zinc-700 hover:bg-zinc-100 font-medium'
                    }`}
                  >
                    v{version}
                    {index === 0 && selectedVersion !== version && (
                      <span className="ml-2 text-xs text-zinc-400">latest</span>
                    )}
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {selectedVersion && (
              <>
                <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
                  Version {selectedVersion}
                  {filteredVersions[0] === selectedVersion && (
                    <Badge variant="accent" size="sm" glow>
                      <Sparkles className="h-3 w-3 mr-1" />
                      Latest
                    </Badge>
                  )}
                </h2>

                {selectedVersionReleases.length === 0 ? (
                  <Card>
                    <CardContent>
                      <EmptyState
                        icon={<Package className="h-10 w-10" />}
                        title="No releases for this platform"
                        description="Try selecting a different platform filter."
                        size="sm"
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {selectedVersionReleases.map((release, index) => (
                      <Card
                        key={release.releaseId}
                        className="group animate-fade-in overflow-hidden"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <CardContent className="p-6 relative">
                          {/* Gradient accent on hover */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-accent-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                          <div className="relative">
                            <div className="flex items-start gap-4 mb-4">
                              <div
                                className={`flex items-center justify-center w-14 h-14 rounded-2xl transition-all duration-300 group-hover:scale-105 ${
                                  release.platform === 'macos'
                                    ? 'bg-zinc-100'
                                    : release.platform === 'windows'
                                    ? 'bg-blue-50'
                                    : 'bg-orange-50'
                                }`}
                              >
                                <PlatformIcon
                                  platform={release.platform}
                                  className="h-8 w-8"
                                  colored
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-bold text-zinc-900">
                                    {getPlatformName(release.platform)}
                                  </h3>
                                  <Badge
                                    variant={
                                      release.releaseType === 'stable'
                                        ? 'success'
                                        : release.releaseType === 'beta'
                                        ? 'warning'
                                        : 'default'
                                    }
                                    size="sm"
                                    glow={release.releaseType === 'stable'}
                                  >
                                    {release.releaseType === 'stable' && (
                                      <Sparkles className="h-3 w-3 mr-1" />
                                    )}
                                    {release.releaseType}
                                  </Badge>
                                </div>
                                <p className="text-sm text-zinc-500 font-medium">
                                  {release.architecture}
                                </p>
                              </div>
                            </div>

                            <div className="text-sm text-zinc-500 mb-4 space-y-1 bg-zinc-50/50 rounded-lg p-3">
                              <p className="flex items-center justify-between">
                                <span className="text-zinc-400">Size</span>
                                <span className="font-medium text-zinc-700">
                                  {formatFileSize(release.fileSize)}
                                </span>
                              </p>
                              {release.publishedAt && (
                                <p className="flex items-center justify-between">
                                  <span className="text-zinc-400">Published</span>
                                  <span className="font-medium text-zinc-700">
                                    {new Date(
                                      release.publishedAt
                                    ).toLocaleDateString(undefined, {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </span>
                                </p>
                              )}
                            </div>

                            {release.releaseNotes && (
                              <p className="text-sm text-zinc-600 mb-4 line-clamp-2">
                                {release.releaseNotes}
                              </p>
                            )}

                            <Button
                              onClick={() => handleDownload(release)}
                              loading={downloading === release.releaseId}
                              className="w-full group/btn"
                              leftIcon={
                                downloading !== release.releaseId ? (
                                  <Download className="h-4 w-4 group-hover/btn:animate-bounce" />
                                ) : undefined
                              }
                            >
                              {downloading === release.releaseId
                                ? 'Preparing...'
                                : 'Download'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Release Notes Section */}
                {selectedVersionReleases[0]?.releaseNotes && (
                  <Card className="mt-6" accent="gradient">
                    <CardContent className="p-6">
                      <h3 className="text-sm font-bold text-zinc-900 mb-3">
                        Release Notes
                      </h3>
                      <p className="text-sm text-zinc-600 whitespace-pre-wrap leading-relaxed">
                        {selectedVersionReleases[0].releaseNotes}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DownloadsPage;
